/**
 * <SamMissionCard> — la mission de Sam, sur l'accueil de l'élève, un jour sans live (molecule).
 *
 * Avant une séance, l'élève a des activités à faire pour elle. Sur l'accueil, c'est la mission de
 * Sam, dite comme du jeu : combien d'activités, combien sont faites (la barre segmentée du livre,
 * une étape par activité), et le bouton qui la lance ou la reprend. Pas « préparer sa séance ».
 *
 * Sa carte, sous celle du prochain live et distincte d'elle. La carte du live est encadrée du
 * dégradé des temps forts et montre le tuteur en photo ; celle-ci n'a pas de cadre (un seul par
 * écran), elle porte l'aurore des champions (le livre la pose « là où un champion a agi ») et
 * montre la tête de Sam en grand. Pas de violet, pas de bleu de Sam en aplat : sa couleur ne peint
 * que son nœud.
 *
 * Trois états, lus sur `done` et `total` :
 *  - rien de fait : « 5 activités avant ta séance avec Camille », la barre éteinte, « Je lance la
 *    mission » ;
 *  - en cours : « Plus que 3 activités avant ta séance avec Camille », deux étapes allumées,
 *    « Je continue » ;
 *  - tout fait : « Mission accomplie », la barre au vert de l'acquis, et plus de bouton. La carte
 *    reste jusqu'au live : c'est la récompense, et ce qui dit à l'élève qu'il est prêt.
 * Une mission sans activité (`total` nul) ne se rend pas.
 *
 * **Sans mission** (`kind: "study"`), Sam reste là, comme l'ancien bandeau (« Étudie avec Sam en
 * attendant ») : « Étudier avec Sam », « Étudie avec Sam en attendant ta séance avec Camille », sans
 * barre, et « Je commence », qui ouvre sa prochaine activité avec lui.
 *
 * Le bouton passe à droite quand le conteneur est large, dessous sinon.
 *
 * Composes DS pieces only — `<Card>`, `<AuroraFill>`, `<ChampionAvatar>`, `<SectionLabel>`,
 * `<Title>`, `<SegmentedBar>`, `<Text>`, `<Button>`.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { ChampionAvatar } from "../../atoms/Avatar/ChampionAvatar"
import { Button } from "../../atoms/Buttons/Button"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { SegmentedBar, STEP_BAR_HEIGHT, stepSegments } from "../../atoms/SegmentedBar"
import { SectionLabel } from "../../atoms/typography/SectionLabel"
import { Text } from "../../atoms/typography/Text"
import { Title } from "../../atoms/typography/Title"
import { useWidthAtLeast } from "../../hooks/useWidthAtLeast"
import { frenchSpacing } from "../../utils/frenchSpacing"
import { type UITutor } from "../../../ports/primary/ui/types"
import { type UISamMission } from "../../../ports/primary/ui/types/studentHome"
import { AuroraFill } from "../AuroraFill"
import { Card } from "../Card"

/** La mission de Sam pour le prochain live (`ports/primary/ui/types/studentHome`), réexportée pour ses lecteurs du DS. */
export type { UISamMission }

export type SamMissionCardProps = UISamMission & {
    /** Le tuteur du prochain live : la mission se fait avant SA séance. */
    tutor: UITutor
    /**
     * « Je lance la mission », « Je continue » : l'écran ouvre la prochaine activité de la mission, avec
     * Sam. « Je commence » (sans mission) : sa prochaine activité, avec Sam.
     */
    onStart: () => void
    testID?: string
}

const activities = (count: number) => `${count} ${count > 1 ? "activités" : "activité"}`

export const SAM_MISSION_TEXTS = {
    label: "Mission de Sam",
    start: "Je lance la mission",
    resume: "Je continue",
    todo: (total: number, tutor: string) =>
        `${total > 1 ? activities(total) : "Une activité"} avant ta séance avec ${tutor}`,
    left: (left: number, tutor: string) =>
        `${left > 1 ? `Plus que ${activities(left)}` : "Plus qu'une activité"} avant ta séance avec ${tutor}`,
    accomplished: "Mission accomplie",
    studyLabel: "Étudier avec Sam",
    study: (tutor: string) => `Étudie avec Sam en attendant ta séance avec ${tutor}`,
    studyHint: "Il t'accompagne sur ta prochaine activité.",
    studyStart: "Je commence",
    ready: (tutor: string) => `Tout est fait avant ta séance avec ${tutor}.`,
    progress: (done: number, total: number) => `${done}/${total}`,
    progressLabel: (done: number, total: number) => `${done} sur ${activities(total)} faite${done > 1 ? "s" : ""}`,
}

/** À partir de cette largeur de carte, le bouton tient à droite du texte sans le replier. */
export const SAM_MISSION_BESIDE_FROM = 520

/** La tête de Sam : la plus grande taille du livre sur un téléphone, plus grande au large. */
export const SAM_HEAD_SIZE = { narrow: 72, wide: 88 }

/** Au large, la barre s'arrête là : étirée sur toute la carte, son compte tombait loin du titre. */
export const SAM_MISSION_PROGRESS_MAX = 480

export function SamMissionCard(props: SamMissionCardProps) {
    const { done, total, kind = "mission", tutor, onStart, testID } = props
    const { onLayout, atLeast: wide } = useWidthAtLeast(SAM_MISSION_BESIDE_FROM)

    const study = kind === "study"
    if (!study && total <= 0) return null

    const count = Math.max(0, Math.min(done, total))
    const accomplished = !study && count >= total
    const headSize = wide ? SAM_HEAD_SIZE.wide : SAM_HEAD_SIZE.narrow
    const title = study
        ? SAM_MISSION_TEXTS.study(tutor.firstname)
        : accomplished
          ? SAM_MISSION_TEXTS.accomplished
          : count === 0
            ? SAM_MISSION_TEXTS.todo(total, tutor.firstname)
            : SAM_MISSION_TEXTS.left(total - count, tutor.firstname)
    const hint = study ? SAM_MISSION_TEXTS.studyHint : accomplished ? SAM_MISSION_TEXTS.ready(tutor.firstname) : null
    const startLabel = study
        ? SAM_MISSION_TEXTS.studyStart
        : count === 0
          ? SAM_MISSION_TEXTS.start
          : SAM_MISSION_TEXTS.resume

    return (
        <Card p={wide ? "6" : "4"} testID={testID}>
            <AuroraFill />
            <View style={styles.split(wide)} onLayout={onLayout}>
                <Row gap="4" align="center" style={[styles.bow(headSize), wide ? styles.fill : undefined]}>
                    <ChampionAvatar name="sam" size={headSize} />
                    <Stack gap="3" style={styles.fill}>
                        <Stack gap="1">
                            <SectionLabel>
                                {study ? SAM_MISSION_TEXTS.studyLabel : SAM_MISSION_TEXTS.label}
                            </SectionLabel>
                            <Title size={wide ? "text-2xl" : "text-xl"}>{frenchSpacing(title)}</Title>
                            {hint != null && (
                                <Text size="text-md" color="text-muted">
                                    {hint}
                                </Text>
                            )}
                        </Stack>
                        {!study && (
                            <View
                                accessible
                                accessibilityLabel={SAM_MISSION_TEXTS.progressLabel(count, total)}
                                style={styles.progress}
                            >
                                <Row gap="3" align="center">
                                    <View style={styles.fill}>
                                        <SegmentedBar
                                            max={total}
                                            height={STEP_BAR_HEIGHT}
                                            gap="1"
                                            segments={stepSegments(total, count, accomplished ? "success" : "role")}
                                        />
                                    </View>
                                    <Text
                                        size="text-sm"
                                        weight="bold"
                                        color={accomplished ? "success-strong" : "text-primary"}
                                    >
                                        {SAM_MISSION_TEXTS.progress(count, total)}
                                    </Text>
                                </Row>
                            </View>
                        )}
                    </Stack>
                </Row>
                {!accomplished && <Button label={startLabel} onPress={onStart} fullWidth={!wide} />}
            </View>
        </Card>
    )
}

const styles = StyleSheet.create((theme) => ({
    /** Large : le contenu à gauche, le bouton à droite ; étroit : le bouton dessous, pleine largeur. */
    split: (beside: boolean) => ({
        flexDirection: beside ? ("row" as const) : ("column" as const),
        alignItems: beside ? ("center" as const) : ("stretch" as const),
        gap: theme.spacing[beside ? "6" : "4"],
    }),
    fill: { flex: 1, flexShrink: 1, minWidth: 0 },
    progress: { maxWidth: SAM_MISSION_PROGRESS_MAX },
    /** Le nœud de Sam pend sous son disque. */
    bow: (size: number) => ({ paddingBottom: size / 14 }),
}))
